import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationFunctions } from './EducationFunctions'
import { HomologateControl } from './HomologateControl'
import { SchoolControl } from './SchoolControl'
import { UniversityControl } from './UniversityControl'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { Tips } from '@/components/common'
import { FormNavigation } from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import mockDropdowns from '@/data/forms/mockDropdowns.json'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { EducationFormData } from '@/types/forms/educationForm'
import { FormsGroupsProps } from '@/types/forms/formStatus'
import { OptionsField } from '@/types/forms/optionsField'

// auxiliary function to get and filter the titles

// --- Helpers extraídos para reducir líneas/complejidad del componente ---
function getDynamicIcfesScoreKeys(
    formData: EducationFormData,
    modality: number
): string[] {
    const inSchoolModality = modality === 15 || modality === 16
    const received = (formData as any).isIcfesReceived === true

    if (!inSchoolModality || !received) {
        return []
    }

    const scores = (formData as any).icfesScores || {}
    const dynamicScoreKeys = Object.keys(scores).filter((k) => {
        const key = k.toUpperCase()

        return (
            !key.includes('SNP') &&
            !key.includes('REGISTRO') &&
            !key.includes('FECHA')
        )
    })

    return dynamicScoreKeys.map((k) => `icfesScores.${k}`)
}

function isFieldFilled(formData: EducationFormData, field: string): boolean {
    const value = (formData as any)[field]

    if (typeof value === 'string') {
        return value.trim().length > 0
    }
    if (typeof value === 'boolean') {
        return true // Un booleano siempre está "completado", sea true o false
    }
    if (value === undefined && field.startsWith('icfesScores.')) {
        const [, key] = field.split('.')
        const scores = (formData as any).icfesScores || {}
        const sv = scores[key]

        if (typeof sv === 'string') {
            return sv.trim().length > 0
        }

        return sv !== null && sv !== undefined && sv !== ''
    }

    return value !== null && value !== undefined && value !== ''
}

function calculateEducationProgressHelper(
    formData: EducationFormData,
    modality: number,
    isExternal: boolean
): number {
    let required: string[] =
        modality === 15 || modality === 16
            ? ['schoolId', 'titleSchool', 'schoolStudyStatus']
            : ['universityId', 'universityTitle', 'universityStudyStatus']

    if (
        (modality === 15 || modality === 16) &&
        formData.isIcfesReceived === true
    ) {
        required.push('icfesType')

        // Si el tipo de examen es "06" (Examen Extranjero), requerir foreignExam
        if (formData.icfesType === '06') {
            required.push('foreignExam')
        } else {
            // Para otros tipos, requerir ID, fecha y añadir campos dinámicos de puntajes
            required.push('icfesUniqueId')
            required.push('icfesExamDate')
            required = required.concat(
                getDynamicIcfesScoreKeys(formData, modality)
            )
        }
    }

    if (isExternal) {
        required.push('educationStatus')
        if (formData.educationStatus === 'Graduado') {
            required.push('graduationDate')
        }
    }

    const filled = required.filter((f) => isFieldFilled(formData, f))

    return required.length
        ? Math.round((filled.length / required.length) * 100)
        : 0
}

function buildIcfesPayloadHelper(data: EducationFormData) {
    const icfesTypeCode = String(data.icfesType).padStart(2, '0')

    console.log('buildIcfesPayloadHelper - data:', data)
    console.log('buildIcfesPayloadHelper - icfesTypeCode:', icfesTypeCode)
    console.log('buildIcfesPayloadHelper - data.foreignExam:', data.foreignExam)

    // Si es examen extranjero (código "06"), enviar el código del examen como puntaje
    if (icfesTypeCode === '06') {
        const payload = {
            icfesTypeCode,
            numeroRegistro: data.icfesUniqueId || '',
            fechaIcfes: data.icfesExamDate || '',
            puntajes: {
                // El backend espera el código del examen (ej: "055") como el puntaje del área ZZ_EXTRANJERO
                ZZ_EXTRANJERO: data.foreignExam || ''
            }
        }

        console.log(
            'buildIcfesPayloadHelper - payload para examen extranjero:',
            payload
        )

        return payload
    }

    // Para exámenes nacionales, procesar los puntajes como antes
    const scores = (data as any).icfesScores || {}
    const puntajes: Record<string, string> = {}
    let puntajeGlobal = ''
    let puesto = ''
    let percentil = ''

    Object.entries(scores).forEach(([key, value]) => {
        const val = value !== null && value !== undefined ? String(value) : ''

        if (!val) {
            return
        }
        const k = key.toUpperCase()

        if (
            k.includes('FECHA') ||
            k.includes('SNP') ||
            k.includes('REGISTRO')
        ) {
            return
        }
        if (
            k.includes('GLOBAL') ||
            (k.includes('PUNTAJE') && !k.includes('PERCENTIL'))
        ) {
            puntajeGlobal = val

            return
        }
        if (k.includes('PUESTO')) {
            puesto = val

            return
        }
        if (k.includes('PERCENTIL')) {
            percentil = val

            return
        }
        puntajes[key] = val
    })

    const payload = {
        icfesTypeCode,
        numeroRegistro: data.icfesUniqueId || '',
        fechaIcfes: data.icfesExamDate || '',
        puntajes,
        ...(puntajeGlobal ? { puntaje: puntajeGlobal, puntajeGlobal } : {}),
        ...(puesto ? { puesto } : {}),
        ...(percentil ? { percentil } : {})
    }

    console.log(
        'buildIcfesPayloadHelper - payload para examen nacional:',
        payload
    )

    return payload
}

// Helper function to clean form data based on modality
const cleanFormData = (data: EducationFormData, modality: number) => {
    const cleanedData = { ...data }
    const fieldsToDelete =
        modality === 15 || modality === 16
            ? [
                  'universityName',
                  'universityTitle',
                  'universityStudyStatus',
                  'examType',
                  'examResult',
                  'universityCountry',
                  'universityDepartment',
                  'universityMunicipality'
              ]
            : [
                  'schoolName',
                  'titleSchool',
                  'schoolStudyStatus',
                  'isIcfesReceived',
                  'icfesUniqueId',
                  'countrySchool',
                  'departmentSchool',
                  'municipalitySchool'
              ]

    fieldsToDelete.forEach(
        (field) => delete cleanedData[field as keyof EducationFormData]
    )

    return cleanedData
}

// Helper function to load and process education data
const loadEducationData = async (
    idAspirant: number,
    aspirantsApi: any,
    showLoader: () => void,
    hideLoader: () => void,
    reset: (data: any) => void,
    excludeFields: (modality: number, admissionType: number) => any,
    setIsExternal: (value: boolean) => void,
    setModality: (value: number) => void,
    setExcludedFields: (value: string[]) => void,
    getEducationOptions: () => Promise<any>,
    setSchoolTitles: (opts: OptionsField[]) => void,
    setUniversityTitles: (opts: OptionsField[]) => void,
    setStudyStatusOptions: (opts: OptionsField[]) => void
) => {
    showLoader()
    const { data } = await aspirantsApi.getEducationData(idAspirant)

    // Map saved codes to IDs for autocomplete validation/display
    if (data.schoolCode) {
        data.schoolId = data.schoolCode
        // Si el código es "OTHER_INSTITUTION", copiar el nombre al campo personalizado
        if (data.schoolCode === 'OTHER_INSTITUTION' && data.schoolName) {
            data.customSchoolName = data.schoolName
            console.log(
                'EducationForm - Setting customSchoolName:',
                data.customSchoolName
            )
        }
    }
    if (data.universityCode) {
        data.universityId = data.universityCode
        // Si el código es "OTHER_INSTITUTION", copiar el nombre al campo personalizado
        if (
            data.universityCode === 'OTHER_INSTITUTION' &&
            data.universityName
        ) {
            data.customUniversityName = data.universityName
            console.log(
                'EducationForm - Setting customUniversityName:',
                data.customUniversityName
            )
        }
    }

    console.log('EducationForm - Data after mapping:', {
        schoolId: data.schoolId,
        schoolName: data.schoolName,
        customSchoolName: data.customSchoolName,
        universityId: data.universityId,
        universityName: data.universityName,
        customUniversityName: data.customUniversityName
    })

    const { external, completeExcludeFields } = excludeFields(
        Number(data.educationModality),
        Number(data.admissionType)
    )

    setIsExternal(external)
    setModality(Number(data.educationModality))
    setExcludedFields(completeExcludeFields)

    // Load options BEFORE resetting form to avoid "Keys not present in collection" error
    const { schoolTitles, universityTitles, studyStatusOptions } =
        await getEducationOptions()

    setSchoolTitles(schoolTitles)
    setUniversityTitles(universityTitles)
    setStudyStatusOptions(studyStatusOptions)

    // Now reset the form with the data after options are loaded
    reset(data)

    hideLoader()
}

export const EducationForm = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): React.ReactElement => {
    const { t } = useTranslation()
    const { showToast } = useToast()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { idAspirant } = useAuthHook()
    const { idAsesor } = useAuthStore()
    const { showLoader, hideLoader } = useProviderLoader()
    const { control, handleSubmit, watch, reset, setValue } =
        useForm<EducationFormData>()
    const { excludeFields, getEducationOptions } = EducationFunctions()

    const [isExternal, setIsExternal] = useState<boolean>(false)
    const [modality, setModality] = useState<number>(0)
    const [excludedFields, setExcludedFields] = useState<string[]>()
    const [schoolTitles, setSchoolTitles] = useState<OptionsField[]>([])
    const [universityTitles, setUniversityTitles] = useState<OptionsField[]>([])
    const [studyStatusOptions, setStudyStatusOptions] = useState<
        OptionsField[]
    >([])
    const text = 'educationInformation'

    useEffect(() => {
        if (!idAspirant) {
            return
        }
        loadEducationData(
            idAspirant,
            aspirantsApi,
            showLoader,
            hideLoader,
            reset,
            excludeFields,
            setIsExternal,
            setModality,
            setExcludedFields,
            getEducationOptions,
            setSchoolTitles,
            setUniversityTitles,
            setStudyStatusOptions
        )
    }, [idAspirant, reset])

    const progress = calculateEducationProgressHelper(
        watch(),
        modality,
        isExternal
    )

    const onSubmit = async (data: EducationFormData): Promise<void> => {
        if (!idAspirant) {
            return
        }
        showLoader()

        const payload = { ...data }

        // Map schoolId to schoolCode and universityId to universityCode for backend
        if (payload.schoolId) {
            ;(payload as any).schoolCode = payload.schoolId
            delete payload.schoolId
        }
        if (payload.universityId) {
            ;(payload as any).universityCode = payload.universityId
            delete payload.universityId
        }

        const response = await aspirantsApi.postEducationData(
            idAspirant,
            payload,
            idAsesor || 0
        )

        hideLoader()
        if (response.status === 200) {
            // Si hay ICFES y tipo seleccionado, enviar al endpoint nuevo
            if (data.isIcfesReceived && data.icfesType) {
                await aspirantsApi.postIcfesForm(
                    idAspirant,
                    buildIcfesPayloadHelper(data),
                    idAsesor || 0
                )
            }
            showToast({
                title: t('basicDataInformation.title'),
                message: t('forms.ui.save'),
                type: 'success'
            })
            onNext()
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form className="space-y-4">
                <Tips text={t('tips.searchInstitution')} />
                {modality === 15 || modality === 16 ? (
                    <SchoolControl
                        control={control}
                        text={text}
                        optionsTittles={schoolTitles}
                        watchIcfes={watch('isIcfesReceived')}
                        watchSchool={watch('schoolId')}
                        initialName={watch('schoolName')}
                        setValue={setValue}
                        studyStatusOptions={studyStatusOptions}
                        items={{
                            country: watch('countrySchool'),
                            department: watch('departmentSchool'),
                            city: watch('municipalitySchool')
                        }}
                    />
                ) : (
                    <UniversityControl
                        control={control}
                        text={text}
                        watchUniversity={watch('universityId')}
                        initialName={watch('universityName')}
                        setValue={setValue}
                        optionsTittles={universityTitles}
                        studyStatusOptions={studyStatusOptions}
                        items={{
                            country: watch('universityCountry'),
                            department: watch('universityDepartment'),
                            city: watch('universityMunicipality')
                        }}
                    />
                )}
                {isExternal && (
                    <HomologateControl
                        control={control}
                        text={text}
                        watchEducationStatus={watch('educationStatus')}
                        optionStatusEducation={mockDropdowns.statusEducation}
                    />
                )}
                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    submitLabel={t('forms.ui.nextPage')}
                />
            </form>
        </div>
    )
}
